// Ozlo Ticker Animation
// Uses requestAnimationFrame for reliable marquee behavior.

const TICKER_PIXELS_PER_SECOND = 60;

function getTickerWrappers(root = document) {
  if (root instanceof Element && root.matches('.ticker-wrapper')) {
    return [root];
  }

  if (root instanceof Element || root instanceof Document) {
    return Array.from(root.querySelectorAll('.ticker-wrapper'));
  }

  return Array.from(document.querySelectorAll('.ticker-wrapper'));
}

function updateTickerHeight(root = document) {
  const tickerContainers =
    root instanceof Element || root instanceof Document
      ? root.querySelectorAll('.ticker-container')
      : document.querySelectorAll('.ticker-container');

  if (!tickerContainers.length) return;

  let maxHeight = 0;
  tickerContainers.forEach((tickerContainer) => {
    maxHeight = Math.max(maxHeight, tickerContainer.offsetHeight);
  });

  if (maxHeight > 0) {
    document.documentElement.style.setProperty('--ticker-height', `${maxHeight}px`);
  }
}

function getItemOuterWidth(item) {
  const styles = window.getComputedStyle(item);
  return item.getBoundingClientRect().width + parseFloat(styles.marginLeft) + parseFloat(styles.marginRight);
}

class TickerController {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.container = wrapper.closest('.ticker-container');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.originalNodes = Array.from(wrapper.children).map((node) => node.cloneNode(true));
    this.loopWidth = 0;
    this.offset = 0;
    this.lastFrameTime = 0;
    this.frameId = null;
    this.paused = false;
    this.rebuildTimeout = null;
    this.isDragging = false;
    this.pointerId = null;
    this.dragStartX = 0;
    this.dragStartOffset = 0;
    this.hasBuilt = false;

    this.handlePointerEnter = this.pause.bind(this);
    this.handlePointerLeave = this.resume.bind(this);
    this.handleFocusIn = this.pause.bind(this);
    this.handleFocusOut = (event) => {
      if (!this.wrapper.contains(event.relatedTarget)) this.resume();
    };
    this.handleReducedMotionChange = () => this.build({ preserveOffset: this.hasBuilt });
    this.handlePointerDown = this.onPointerDown.bind(this);
    this.handlePointerMove = this.onPointerMove.bind(this);
    this.handlePointerUp = this.onPointerUp.bind(this);

    this.attachEvents();
    this.build();
  }

  attachEvents() {
    this.wrapper.addEventListener('mouseenter', this.handlePointerEnter);
    this.wrapper.addEventListener('mouseleave', this.handlePointerLeave);
    this.wrapper.addEventListener('focusin', this.handleFocusIn);
    this.wrapper.addEventListener('focusout', this.handleFocusOut);
    this.wrapper.addEventListener('pointerdown', this.handlePointerDown);
    this.wrapper.addEventListener('pointermove', this.handlePointerMove);
    this.wrapper.addEventListener('pointerup', this.handlePointerUp);
    this.wrapper.addEventListener('pointercancel', this.handlePointerUp);
    this.wrapper.addEventListener('lostpointercapture', this.handlePointerUp);

    if (typeof this.reducedMotion.addEventListener === 'function') {
      this.reducedMotion.addEventListener('change', this.handleReducedMotionChange);
    } else if (typeof this.reducedMotion.addListener === 'function') {
      this.reducedMotion.addListener(this.handleReducedMotionChange);
    }

    this.wrapper.querySelectorAll('img').forEach((image) => {
      if (image.complete) return;
      image.addEventListener('load', () => this.scheduleBuild({ preserveOffset: true }), { once: true });
    });

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.scheduleBuild({ preserveOffset: true }));
    }
  }

  resetContent() {
    this.wrapper.replaceChildren(...this.originalNodes.map((node) => node.cloneNode(true)));
    this.wrapper.querySelectorAll('img').forEach((image) => {
      image.draggable = false;
    });
  }

  cloneItemsForLoop() {
    const originalItems = Array.from(this.wrapper.children);
    if (!originalItems.length) return;

    const minimumTrackWidth = this.container.offsetWidth + this.loopWidth;
    let currentWidth = this.loopWidth;
    let safetyCounter = 0;

    while (currentWidth < minimumTrackWidth && safetyCounter < 10) {
      originalItems.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.dataset.tickerClone = 'true';
        this.wrapper.appendChild(clone);
      });

      currentWidth += this.loopWidth;
      safetyCounter += 1;
    }
  }

  measureLoopWidth() {
    const items = Array.from(this.wrapper.children).filter((item) => !item.dataset.tickerClone);
    this.loopWidth = items.reduce((total, item) => total + getItemOuterWidth(item), 0);
  }

  normalizeOffset(offset) {
    if (!this.loopWidth) return 0;

    let normalizedOffset = offset % this.loopWidth;
    if (normalizedOffset > 0) {
      normalizedOffset -= this.loopWidth;
    }

    return normalizedOffset;
  }

  applyOffset() {
    this.wrapper.style.transform = `translate3d(${this.offset}px, 0, 0)`;
  }

  tick(timestamp) {
    if (!this.frameId) return;

    if (this.paused || document.hidden || this.reducedMotion.matches || !this.loopWidth) {
      this.lastFrameTime = timestamp;
      this.frameId = window.requestAnimationFrame((time) => this.tick(time));
      return;
    }

    if (!this.lastFrameTime) this.lastFrameTime = timestamp;

    const elapsedSeconds = (timestamp - this.lastFrameTime) / 1000;
    this.lastFrameTime = timestamp;
    this.offset = this.normalizeOffset(this.offset - (TICKER_PIXELS_PER_SECOND * elapsedSeconds));

    this.applyOffset();
    this.frameId = window.requestAnimationFrame((time) => this.tick(time));
  }

  start() {
    if (this.frameId || !this.loopWidth) return;
    this.wrapper.style.animation = 'none';
    this.lastFrameTime = 0;
    this.frameId = window.requestAnimationFrame((time) => this.tick(time));
  }

  stop() {
    if (this.frameId) {
      window.cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
  }

  pause() {
    this.paused = true;
  }

  resume() {
    if (this.reducedMotion.matches || this.isDragging) return;
    this.paused = false;
  }

  onPointerDown(event) {
    if (event.button !== 0 || !this.loopWidth) return;

    this.isDragging = true;
    this.pointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartOffset = this.offset;
    this.paused = true;
    this.lastFrameTime = 0;
    this.wrapper.classList.add('is-dragging');
    this.wrapper.setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  onPointerMove(event) {
    if (!this.isDragging || this.pointerId !== event.pointerId) return;

    const dragDelta = event.clientX - this.dragStartX;
    this.offset = this.normalizeOffset(this.dragStartOffset + dragDelta);
    this.applyOffset();
    event.preventDefault();
  }

  onPointerUp(event) {
    if (!this.isDragging || this.pointerId !== event.pointerId) return;

    this.isDragging = false;
    this.pointerId = null;
    this.wrapper.classList.remove('is-dragging');

    if (this.wrapper.hasPointerCapture(event.pointerId)) {
      this.wrapper.releasePointerCapture(event.pointerId);
    }

    if (document.hidden || this.reducedMotion.matches || this.wrapper.matches(':hover') || this.wrapper.matches(':focus-within')) {
      this.pause();
    } else {
      this.resume();
    }
  }

  scheduleBuild(options = {}) {
    window.clearTimeout(this.rebuildTimeout);
    this.rebuildTimeout = window.setTimeout(() => this.build(options), 100);
  }

  build(options = {}) {
    const preserveOffset = Boolean(options.preserveOffset && this.hasBuilt && this.loopWidth);
    const previousLoopWidth = this.loopWidth;
    const previousOffset = this.offset;

    this.stop();
    this.resetContent();
    this.wrapper.style.animation = 'none';
    this.offset = preserveOffset ? previousOffset : 0;
    this.applyOffset();
    updateTickerHeight(document);

    window.requestAnimationFrame(() => {
      this.measureLoopWidth();
      this.cloneItemsForLoop();
      this.hasBuilt = true;

      if (preserveOffset && previousLoopWidth && this.loopWidth) {
        const progress = Math.abs(previousOffset) / previousLoopWidth;
        this.offset = this.normalizeOffset(-(progress * this.loopWidth));
      } else {
        this.offset = this.normalizeOffset(this.offset);
      }

      this.applyOffset();

      if (this.reducedMotion.matches || !this.loopWidth) return;
      this.start();
    });
  }

  destroy() {
    this.stop();
    window.clearTimeout(this.rebuildTimeout);

    this.wrapper.removeEventListener('mouseenter', this.handlePointerEnter);
    this.wrapper.removeEventListener('mouseleave', this.handlePointerLeave);
    this.wrapper.removeEventListener('focusin', this.handleFocusIn);
    this.wrapper.removeEventListener('focusout', this.handleFocusOut);
    this.wrapper.removeEventListener('pointerdown', this.handlePointerDown);
    this.wrapper.removeEventListener('pointermove', this.handlePointerMove);
    this.wrapper.removeEventListener('pointerup', this.handlePointerUp);
    this.wrapper.removeEventListener('pointercancel', this.handlePointerUp);
    this.wrapper.removeEventListener('lostpointercapture', this.handlePointerUp);

    if (typeof this.reducedMotion.removeEventListener === 'function') {
      this.reducedMotion.removeEventListener('change', this.handleReducedMotionChange);
    } else if (typeof this.reducedMotion.removeListener === 'function') {
      this.reducedMotion.removeListener(this.handleReducedMotionChange);
    }

    this.resetContent();
    this.wrapper.style.animation = '';
    this.wrapper.style.transform = '';
    this.wrapper.classList.remove('is-dragging');
  }
}

function initTicker(root = document) {
  const tickers = getTickerWrappers(root);

  if (!tickers.length) return;

  tickers.forEach((ticker) => {
    const existingController = window.__ozloTickerControllers.get(ticker);
    if (existingController) {
      existingController.scheduleBuild({ preserveOffset: true });
      return;
    }

    window.__ozloTickerControllers.set(ticker, new TickerController(ticker));
  });
}

function destroyTicker(root = document) {
  getTickerWrappers(root).forEach((ticker) => {
    const controller = window.__ozloTickerControllers.get(ticker);
    if (!controller) return;
    controller.destroy();
    window.__ozloTickerControllers.delete(ticker);
  });
}

if (!window.__ozloTickerInitialized) {
  window.__ozloTickerInitialized = true;
  window.__ozloTickerControllers = new WeakMap();

  let resizeTimeout;

  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimeout);
    resizeTimeout = window.setTimeout(() => initTicker(document), 150);
  });

  document.addEventListener('visibilitychange', () => {
    getTickerWrappers(document).forEach((ticker) => {
      const controller = window.__ozloTickerControllers.get(ticker);
      if (!controller) return;

      if (document.hidden) {
        controller.pause();
      } else {
        controller.resume();
      }
    });
  });

  document.addEventListener('shopify:section:load', (event) => {
    initTicker(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyTicker(event.target);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initTicker(document), { once: true });
} else {
  initTicker(document);
}
